/**
 * Billing source classification for KiedyNabory settlements admin panel.
 *
 * This is the SSOT (Single Source of Truth) for billing source labels,
 * badge colors, and filter mappings. Used in both the customers list column
 * and the SubscriptionDetailsModal "Źródło" field.
 *
 * Mirror of backend KiedyNaboryCustomerDto.billingSource field (AC-1).
 */

/** Billing source enum — mirrors backend BillingSourceKind (AC-1). */
export type BillingSourceKind =
  | "stripe"
  | "legacy_bank_transfer"
  | "legacy_unknown"
  | "billing_bc";

/**
 * The two enum values grouped under the "legacy" UI filter option (AC-5).
 * Using a typed const ensures that if BillingSourceKind changes, TS catches mismatches.
 */
export const LEGACY_BILLING_SOURCE_VALUES = [
  "legacy_bank_transfer",
  "legacy_unknown",
] as const satisfies readonly BillingSourceKind[];

/**
 * Filter type for the billing source dropdown.
 * "legacy" is a UI-only grouping that maps to LEGACY_BILLING_SOURCE_VALUES.
 * "" means "show all".
 */
export type BillingSourceFilter = BillingSourceKind | "legacy" | "";

/**
 * Format a billingSource enum value into a display label and Tailwind className.
 *
 * For null (no active subscription), returns a neutral dash span — NOT a badge.
 * For all enum values, returns a badge-style className.
 *
 * @param billingSource - The billing source value or null
 * @returns Object with `label` (display text) and `className` (Tailwind classes)
 */
export function formatBillingSource(
  billingSource: BillingSourceKind | null
): { label: string; className: string } {
  switch (billingSource) {
    case "stripe":
      return { label: "Stripe", className: "bg-purple-100 text-purple-700" };
    case "legacy_bank_transfer":
      return {
        label: "Stary (przelew)",
        className: "bg-amber-100 text-amber-700",
      };
    case "legacy_unknown":
      return {
        label: "Stary (nieznany)",
        className: "bg-amber-100 text-amber-700",
      };
    case "billing_bc":
      return { label: "Nowy billing", className: "bg-teal-100 text-teal-700" };
    case null:
    default:
      return { label: "—", className: "text-neutral-400" };
  }
}
